import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Backdrop,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Divider,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Phone, 
  Lock,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useLogin } from '../hooks/useLogin';

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, isLoading, error, setError } = useLogin();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);
  const [touched, setTouched] = useState({ phone: false, password: false });

  useEffect(() => {
    if (error) setShowError(true);
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ phone: true, password: true });
    
    if (!phoneNumber || phoneNumber.length < 8) return;
    if (!password || password.length < 3) return;
    
    const result = await login(phoneNumber, password);
    if (result?.success) {
      navigate('/dashboard');
    }
  };

  const handleCloseError = () => {
    setShowError(false);
    if (setError) setError(null);
  };

  const isPhoneValid = phoneNumber && phoneNumber.length >= 8;
  const isPasswordValid = password && password.length >= 3;
  const canSubmit = isPhoneValid && isPasswordValid && !isLoading;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          width: { xs: 200, md: 400 },
          height: { xs: 200, md: 400 },
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.1),
          top: -100,
          right: -100,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: { xs: 150, md: 300 },
          height: { xs: 150, md: 300 },
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.05),
          bottom: -50,
          left: -50,
        }}
      />

      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography sx={{ mt: 2 }}>{t('login')}...</Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error || t('error500')}
        </Alert>
      </Snackbar>

      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
              overflow: 'visible',
              position: 'relative',
              mx: { xs: 1, sm: 2 },
            }}
          >
            {/* Logo Section */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                py: { xs: 3, md: 5 },
                textAlign: 'center',
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box
                component="img"
                src="./assets/window_logo.png"
                alt="Ropera Virtual"
                sx={{ 
                  height: { xs: 60, md: 90 }, 
                  mb: 2,
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="primary"
                sx={{ letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Ropera Virtual
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('welcome')}
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Box component="form" onSubmit={handleSubmit}>
                {/* Phone Number Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    mb={1}
                    color="text.primary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Phone fontSize="small" color="primary" />
                    {t('phonenumber')}
                  </Typography>
                  <PhoneInput
                    country="sy"
                    value={phoneNumber}
                    onChange={(value) => {
                      setPhoneNumber(value);
                      setTouched(prev => ({ ...prev, phone: true }));
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, phone: true }))}
                    inputStyle={{
                      width: '100%',
                      height: 56,
                      fontSize: 16,
                      borderRadius: 12,
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      borderColor: touched.phone && !isPhoneValid 
                        ? theme.palette.error.main 
                        : alpha(theme.palette.divider, 0.3),
                      transition: 'all 0.2s ease',
                    }}
                    containerStyle={{ width: '100%' }}
                    buttonStyle={{ 
                      borderRadius: '12px 0 0 12px',
                      backgroundColor: theme.palette.background.paper,
                      borderColor: touched.phone && !isPhoneValid 
                        ? theme.palette.error.main 
                        : alpha(theme.palette.divider, 0.3),
                    }}
                    dropdownStyle={{
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}
                    searchStyle={{
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}
                    inputProps={{
                      required: true,
                    }}
                  />
                  {touched.phone && !isPhoneValid && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {t('invalidPhone')}
                    </Typography>
                  )}
                </Box>

                {/* Password Field */}
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    mb={1}
                    color="text.primary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Lock fontSize="small" color="primary" />
                    {t('password')}
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setTouched(prev => ({ ...prev, password: true }));
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                    placeholder="••••••••"
                    error={touched.password && !isPasswordValid}
                    helperText={touched.password && !isPasswordValid ? t('passwordRequired') : ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ 
                              color: 'text.secondary',
                              '&:hover': { color: 'primary.main' }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                        }
                      },
                    }}
                  />
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!canSubmit}
                  startIcon={<LoginIcon />}
                  sx={{
                    py: 1.75,
                    fontSize: 16,
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    }
                  }}
                >
                  {t('login')}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('forgotpassword')}
                  </Typography>
                </Divider>

                <Button 
                  fullWidth 
                  variant="text" 
                  color="primary"
                  sx={{ 
                    borderRadius: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }
                  }}
                >
                  {t('support')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>
            Powered by Perla Tech
          </Typography>
          <Typography variant="caption" color="white" sx={{ opacity: 0.6, display: 'block', mt: 0.5 }}>
            © {new Date().getFullYear()} Ropera Virtual
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
